import { Component } from '@angular/core'
import { animationFadeInOut } from '@engineering11/ui-lib/e11-animations'
import { AssistantClientToolsService } from '../../services/assistant-client-tools.service'

/**
 * Standalone highlight spotlight host (Epic 4, story 4.9). Renders nothing until the client-tool executor
 * drives `highlight$`; on a rect it renders a fixed full-screen `e11-z-[1000]` layer with a single
 * anchor-fitted spotlight box — the design-contract §Surface 3 treatment (the `box-shadow: 0 0 0 9999px …`
 * dim/cutout + `skin-primary-accent` ring) — and NO controls (distinct from the controls-bearing tour
 * overlay, which is hard-wired to the tour engine). The box is `pointer-events-none` and auto-dismisses.
 *
 * design-contract.md → §Surface 3 (highlight treatment, z-index, reduced-motion).
 */
@Component({
  selector: 'assistant-highlight-overlay',
  template: `
    <div
      *ngIf="clientTools.highlight$ | async as rect"
      class="e11-fixed e11-inset-0 e11-z-[1000] e11-pointer-events-none"
      data-assistant-highlight
      [@animationFadeInOut]
      [@.disabled]="reducedMotion"
    >
      <!-- Spotlight: an anchor-fitted box whose huge box-shadow dims the rest of the screen; the anchor
           reads through, ringed with the accent token. No controls, pointer-transparent, auto-dismisses. -->
      <div
        class="e11-absolute e11-rounded-md e11-pointer-events-none e11-ring-2 e11-ring-skin-primary-accent"
        data-assistant-highlight-box
        [style.top.px]="rect.top"
        [style.left.px]="rect.left"
        [style.width.px]="rect.width"
        [style.height.px]="rect.height"
        [style.boxShadow]="spotlightShadow"
      ></div>
    </div>
  `,
  animations: [animationFadeInOut()],
})
export class AssistantHighlightOverlayComponent {
  /** The dim + spotlight mask: a transparent anchor box with a huge shadow dimming everything else (§Surface 3). */
  readonly spotlightShadow = '0 0 0 9999px rgba(0, 0, 0, 0.6)'

  /** Instant animations under the OS reduced-motion preference (design contract §Shared foundations). */
  readonly reducedMotion = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  constructor(public clientTools: AssistantClientToolsService) {}
}
